// ./src/utils/api/RentalAssetService.ts
//
// Rental-asset service — the account-level catalog of rentable things (cars,
// equipment, furniture). OpResult-native. Managed from the account Library page
// OR from an event's Rentals page: in the latter case a `contextEventId` AUTH
// CONTEXT lets crew with event.operate on that event manage the event's account
// catalog (the empty-picker fix — mirrors CouponTypeService). See em-scaffold-ui.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { Pagination, SortItem } from "./commonApiTypes";

// =============================================================================
// Enum-like constants (mirror the eventsmagic-api boundary/rental.ts)
// =============================================================================

export const RENTAL_PRICING_MODEL = {
  FLAT: "flat",
  PER_DAY: "per_day",
  PER_HOUR: "per_hour",
} as const;

export const RENTAL_PRICING_MODEL_VALUES = Object.values(RENTAL_PRICING_MODEL);

export const RENTAL_PRICING_MODEL_LABELS: Record<string, string> = {
  flat: "Flat rate",
  per_day: "Per day",
  per_hour: "Per hour",
};

/** A venue reference resolved live on read (current name). Null when the venue was removed. */
export interface RentalVenueRef {
  id?: string;
  name?: string | null;
}

/** The vendor/venue name to display: the live value if present, else the stored snapshot. */
export const venueRefName = (ref?: RentalVenueRef | null, fallback?: string | null): string | null =>
  ref?.name ?? fallback ?? null;

// =============================================================================
// GraphQL
// =============================================================================

const RENTAL_ASSET_FIELDS = `
  id
  accountId
  categoryId
  name
  description
  vendorId
  vendorLabel
  vendor { id name }
  defaultPricingModel
  defaultUnitAmount
  currencyCode
  notes
  orderNo
  status
  rights
  createdAt
  updatedAt
`;

const RENTAL_ASSET_LIST_QUERY = gql`
  query RentalAssetList($filter: RentalAssetFilter, $params: PaginationAndSorting) {
    rentalAssetList(filter: $filter, params: $params) {
      code
      data { ${RENTAL_ASSET_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const RENTAL_ASSET_GET_QUERY = gql`
  query RentalAssetGet($id: ID) {
    rentalAssetGet(id: $id) {
      code
      data { ${RENTAL_ASSET_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const RENTAL_ASSET_CREATE_MUTATION = gql`
  mutation RentalAssetCreate($params: RentalAssetInput) {
    rentalAssetCreate(params: $params) {
      code
      data { ${RENTAL_ASSET_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const RENTAL_ASSET_UPDATE_MUTATION = gql`
  mutation RentalAssetUpdate($where: RentalAssetWhereInput, $params: RentalAssetInput) {
    rentalAssetUpdate(where: $where, params: $params) {
      code
      data { ${RENTAL_ASSET_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const RENTAL_ASSET_REMOVE_MUTATION = gql`
  mutation RentalAssetRemove($where: RentalAssetWhereInput) {
    rentalAssetRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

// =============================================================================
// Types (match the eventsmagic-api RentalAsset schema)
// =============================================================================

export interface RentalAsset {
  id?: string;
  accountId?: string | null;
  categoryId?: string | null;
  name?: string | null;
  description?: string | null;
  /** Default vendor — soft ref → venue (kind='vendor'). */
  vendorId?: string | null;
  /** Fallback name snapshot — used only if the vendor was removed. */
  vendorLabel?: string | null;
  /** The vendor venue resolved live (current name); null if removed. */
  vendor?: RentalVenueRef | null;
  defaultPricingModel?: string | null;
  defaultUnitAmount?: number | null;
  currencyCode?: string | null;
  notes?: string | null;
  orderNo?: number | null;
  status?: number;
  rights?: string[] | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface RentalAssetInput {
  /** Auth context (NOT stored): the event page a crew member is acting from. */
  contextEventId?: string | null;
  categoryId?: string | null;
  name?: string | null;
  description?: string | null;
  vendorId?: string | null;
  defaultPricingModel?: string | null;
  defaultUnitAmount?: number | null;
  currencyCode?: string | null;
  notes?: string | null;
  orderNo?: number | null;
  status?: number | null;
}

export interface RentalAssetFilter {
  id?: string[] | null;
  categoryId?: string[] | null;
  status?: number[] | null;
  searchKeyword?: string | null;
  /** Auth/scoping context (NOT a column): the event page the list is shown on. */
  contextEventId?: string | null;
}

export interface RentalAssetWhereInput {
  id?: string | string[];
  contextEventId?: string | null;
}

// =============================================================================
// Service
// =============================================================================

export class RentalAssetService {
  static async list(
    filter?: RentalAssetFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    return ApiService.executeQuery(RENTAL_ASSET_LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(RENTAL_ASSET_GET_QUERY, { id });
  }

  static async create(params: RentalAssetInput): Promise<OpResult> {
    return ApiService.executeMutation(RENTAL_ASSET_CREATE_MUTATION, { params });
  }

  static async update(where: RentalAssetWhereInput, params: RentalAssetInput): Promise<OpResult> {
    return ApiService.executeMutation(RENTAL_ASSET_UPDATE_MUTATION, { where, params });
  }

  static async remove(id: string, contextEventId?: string | null): Promise<OpResult> {
    return ApiService.executeMutation(RENTAL_ASSET_REMOVE_MUTATION, {
      where: { id, contextEventId: contextEventId || undefined },
    });
  }

  /** Mass-set status on every selected asset in one call. */
  static async bulkUpdate(ids: string[], params: RentalAssetInput, contextEventId?: string | null): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(RENTAL_ASSET_UPDATE_MUTATION, {
      where: { id: ids, contextEventId: contextEventId || undefined },
      params,
    });
  }

  /** Remove every selected asset in one call. */
  static async removeMany(ids: string[], contextEventId?: string | null): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(RENTAL_ASSET_REMOVE_MUTATION, {
      where: { id: ids, contextEventId: contextEventId || undefined },
    });
  }
}
